import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SectionId, HOME_ID, ROUTES } from '@/shared/routes';
import useScrollPercent from '@/hooks/useScrollPercent';
import styles from './SectionNav.module.scss';

// Triggers when a section crosses the middle horizontal band of the viewport
const ACTIVE_SECTION_MARGIN = '-45% 0px -45% 0px';

const SectionNav = () => {
  const { t } = useTranslation();

  const [activeId, setActiveId] = useState<SectionId>(HOME_ID);
  const scrollPercent = useScrollPercent();

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    ROUTES.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: ACTIVE_SECTION_MARGIN }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <nav className={styles.nav} aria-label={t('sectionNav.label')}>
      <div className={styles.track}>
        {ROUTES.map(({ id }) => (
          <a
            key={id}
            href={`#${id}`}
            className={`${styles.dotLink}${id === activeId ? ` ${styles.active}` : ''}`}
            aria-label={t(`header.nav.${id}`)}
            aria-current={id === activeId ? 'true' : undefined}
          >
            <span className={styles.label}>{t(`header.nav.${id}`)}</span>
            <span className={styles.dot} />
          </a>
        ))}
      </div>
      <span className={styles.percent} aria-hidden='true'>
        {scrollPercent}%
      </span>
    </nav>
  );
};

export default SectionNav;
